import { type QuoteData } from "../common/modelsV2";
import { produce } from "immer";
import { useReducer } from "react";

import {
  type DocumentListRow,
  orderListPageActions,
  orderListPageReducer,
  type finishLoadingDetails as orderListFinishLoadingDetails,
  type UseOrderListStateActionType,
} from "~/OrdersQuotes/OrderListPage/useOrderListState";
import type { QuoteDetailsMO } from "~/OrdersQuotes/common/components/v2/models";

export interface QuoteListRow extends DocumentListRow<QuoteData> {
  details?: QuoteDetailsMO;
}

export function canStartLoadingDetails(item: DocumentListRow<unknown>) {
  return !item.detailsLoaded && !item.loadingDetails && !item.errorLoadingDetails;
}

export function useQuoteListState(): [QuoteListPageState, (action: ActionType) => void] {
  return useReducer(reducer, quoteInitialState);
}

interface QuoteListPageState {
  loading: boolean;
  error: boolean;
  results: QuoteListRow[];
}

const quoteInitialState: QuoteListPageState = {
  loading: false,
  error: false,
  results: [],
};

function quoteListFinishLoadingDetails(data: QuoteDetailsMO) {
  return {
    type: "finishLoadingDetails",
    payload: {
      data,
    },
  } as const;
}

export const quoteListPageActions = {
  ...orderListPageActions,
  finishLoadingDetails: quoteListFinishLoadingDetails,
};

type ActionType =
  | Exclude<UseOrderListStateActionType, ReturnType<typeof orderListFinishLoadingDetails>>
  | ReturnType<typeof quoteListFinishLoadingDetails>;

function reducer(state: QuoteListPageState, action: ActionType): QuoteListPageState {
  if (action.type !== "finishLoadingDetails") {
    // @ts-ignore
    const newState = orderListPageReducer<ActionType>(state, action);
    if (newState !== state) {
      return newState as QuoteListPageState;
    }
  }

  return produce(state, (draft: typeof state) => {
    switch (action.type) {
      case "finishLoadingDetails": {
        const details = action.payload.data;
        const index = draft.results.findIndex((item) => item.data.soNumber === details.soNumber);
        const row = draft.results[index];
        row.details = details;
        row.loadingDetails = false;
        row.detailsLoaded = true;
        break;
      }
    }
  });
}
